/**
 * Страница Dashboard
 * Красивый современный дизайн с тёмной темой
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AnimatedCard from '../components/AnimatedCard';
import SkeletonLoader from '../components/SkeletonLoader';
import AnimatedCounter from '../components/AnimatedCounter';
import { API_URL } from '../utils/api.js';

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    channels: 0,
    sources: 0,
    videos: 0,
    todayPosts: 0,
  });
  const [youtubeStats, setYoutubeStats] = useState({
    totalUploaded: 0,
    totalTranscribed: 0,
    totalPublishedYouTube: 0,
    totalPublishedTelegram: 0,
  });
  const [limits, setLimits] = useState(null);
  const [health, setHealth] = useState(null);
  const [recentVideos, setRecentVideos] = useState([]);
  const [attentionVideos, setAttentionVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Загружаем все данные при монтировании
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchHealth(),
        fetchRecentVideos(),
        fetchYoutubeStatistics(),
        fetchLimits(),
      ]);
      setLoading(false);
    };
    
    loadAllData();
    
    // Обновляем данные каждые 30 секунд
    const interval = setInterval(() => {
      fetchStats();
      fetchYoutubeStatistics();
      fetchLimits();
      fetchRecentVideos(); // Также обновляем список видео
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      // Не устанавливаем loading для этой функции, чтобы не блокировать UI
      const token = localStorage.getItem('session_token');
      const config = token ? { headers: { 'x-session-token': token }, withCredentials: true } : { withCredentials: true };
      
      const [channelsRes] = await Promise.all([
        axios.get(`${API_URL}/channels`, config).catch(() => ({ data: { channels: [] } })),
      ]);

      setStats(prev => ({
        ...prev,
        channels: channelsRes.data.channels?.length || 0,
      }));
    } catch (error) {
      console.error('Ошибка при загрузке статистики каналов:', error);
    }
  };

  const fetchHealth = async () => {
    try {
      const res = await axios.get(`${API_URL}/health`);
      setHealth(res.data);
    } catch (error) {
      setHealth({ ok: false });
    }
  };

  const fetchRecentVideos = async () => {
    try {
      const res = await axios.get(`${API_URL}/youtube/videos`);
      if (res.data.videos) {
        const videos = res.data.videos;
        setRecentVideos(videos.slice(0, 5));
        
        // Видео, требующие внимания
        const attention = videos.filter(video => 
          video.status === 'error' || 
          video.status === 'publish_error' ||
          video.status === 'transcribing' ||
          video.status === 'generating' ||
          (video.status === 'transcribed' && !video.youtube_url)
        );
        setAttentionVideos(attention.slice(0, 5));
      }
    } catch (error) {
      console.error('Ошибка загрузки видео:', error);
    }
  };

  const fetchYoutubeStatistics = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const config = token ? { headers: { 'x-session-token': token } } : {};
      
      const res = await axios.get(`${API_URL}/youtube/statistics`, {
        ...config,
        withCredentials: true // Отправляем cookies
      });
      
      console.log('[Dashboard] Статистика YouTube получена:', res.data);
      
      if (res.data && !res.data.error) {
        setYoutubeStats({
          totalUploaded: res.data.totalUploaded || 0,
          totalTranscribed: res.data.totalTranscribed || 0,
          totalPublishedYouTube: res.data.totalPublishedYouTube || 0,
          totalPublishedTelegram: res.data.totalPublishedTelegram || 0,
        });
      } else {
        console.warn('[Dashboard] Ошибка в ответе статистики:', res.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки статистики YouTube:', error);
      console.error('Детали ошибки:', error.response?.data);
      // Если ошибка авторизации, не показываем статистику
      if (error.response?.status === 401) {
        setYoutubeStats({
          totalUploaded: 0,
          totalTranscribed: 0,
          totalPublishedYouTube: 0,
          totalPublishedTelegram: 0,
        });
      }
    }
  };

  const fetchLimits = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const config = token ? { headers: { 'x-session-token': token } } : {};
      
      const res = await axios.get(`${API_URL}/youtube/limits`, {
        ...config,
        withCredentials: true // Отправляем cookies
      });
      
      console.log('[Dashboard] Лимиты получены:', res.data);
      
      if (res.data?.limits) {
        setLimits(res.data.limits);
      } else {
        console.warn('[Dashboard] Лимиты не найдены в ответе:', res.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки лимитов:', error);
      console.error('Детали ошибки:', error.response?.data);
      // Если ошибка авторизации, не показываем лимиты
      if (error.response?.status === 401) {
        setLimits(null);
      }
    }
  };

  const statCards = [
    { label: 'Загружено видео', value: youtubeStats.totalUploaded, icon: '🎬', gradient: 'from-purple-500 to-pink-500', link: '/youtube' },
    { label: 'Транскрибировано', value: youtubeStats.totalTranscribed, icon: '🎤', gradient: 'from-blue-500 to-cyan-500', link: '/youtube' },
    { label: 'Опубликовано на YouTube', value: youtubeStats.totalPublishedYouTube, icon: '📺', gradient: 'from-red-500 to-orange-500', link: '/youtube' },
    { label: 'Опубликовано в Telegram', value: youtubeStats.totalPublishedTelegram, icon: '📱', gradient: 'from-green-500 to-emerald-500', link: '/youtube' },
    { label: 'Telegram каналы', value: stats.channels, icon: '📢', gradient: 'from-indigo-500 to-purple-500', link: '/settings?tab=telegram' },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 tracking-tighter">
            👋 Добро пожаловать!
          </h1>
          <p className="text-slate-400 tracking-tight">Панель управления AI Market Hub</p>
        </div>
        
        {/* Статус системы */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`
            flex items-center gap-3 px-4 py-2 rounded-xl border backdrop-blur-sm
            ${health?.ok 
              ? 'bg-green-500/10 border-green-500/30 text-green-400' 
              : 'bg-red-500/10 border-red-500/30 text-red-400'
            }
          `}
        >
          <div className={`w-3 h-3 rounded-full animate-pulse ${health?.ok ? 'bg-green-400' : 'bg-red-400'}`} />
          <span className="text-sm font-medium">
            {health?.ok ? 'Система работает' : 'Система недоступна'}
          </span>
        </motion.div>
      </motion.div>

      {/* Статистика */}
      {loading ? (
        <SkeletonLoader type="stat-card" count={5} className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4" />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {statCards.map((card, index) => (
            <AnimatedCard
              key={card.label}
              onClick={() => card.link && navigate(card.link)}
              hover={!!card.link}
              glow={true}
              className={`
                relative overflow-hidden p-6
                bg-gradient-to-br ${card.gradient} shadow-lg
                ${card.link ? 'cursor-pointer' : ''}
              `}
            >
              {/* Декоративный элемент */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-xl" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)] opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative z-10">
                <span className="text-3xl mb-2 block">{card.icon}</span>
                <div className="text-3xl font-bold text-white mb-1">
                  <AnimatedCounter value={card.value} duration={1000} />
                </div>
                <div className="text-white/80 text-sm">{card.label}</div>
              </div>
            </AnimatedCard>
          ))}
        </div>
      )}

      {/* Виджет дневных лимитов */}
      {limits && (
        <AnimatedCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">📊 Дневные лимиты</h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchLimits}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Обновить
            </motion.button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(limits).map(([key, limit]) => {
              if (!limit || typeof limit !== 'object') return null;
              // API возвращает current и limit, а не used и max
              const used = limit.current || limit.used || 0;
              const max = limit.limit || limit.max || 0;
              const percentage = max > 0 ? (used / max) * 100 : 0;
              const colorClass = percentage < 50 ? 'bg-green-500' : percentage < 80 ? 'bg-yellow-500' : 'bg-red-500';
              
              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">{limit.name || key}</span>
                    <span className="text-white font-semibold">{used}/{max}</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(percentage, 100)}%` }}
                      transition={{ duration: 0.5 }}
                      className={`h-2 rounded-full ${colorClass}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </AnimatedCard>
      )}

      {/* Быстрые действия */}
      <AnimatedCard className="p-6">
        <h2 className="text-xl font-bold text-white mb-4">🚀 Быстрые действия</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/youtube')}
            className="flex items-center gap-4 p-4 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-xl transition-all text-left"
          >
            <div className="w-12 h-12 bg-purple-500/30 rounded-xl flex items-center justify-center text-2xl">
              📹
            </div>
            <div>
              <div className="font-semibold text-white">Загрузить видео</div>
              <div className="text-sm text-gray-400">Добавить новое видео</div>
            </div>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/settings?tab=telegram')}
            className="flex items-center gap-4 p-4 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-xl transition-all text-left"
          >
            <div className="w-12 h-12 bg-blue-500/30 rounded-xl flex items-center justify-center text-2xl">
              📢
            </div>
            <div>
              <div className="font-semibold text-white">Telegram каналы</div>
              <div className="text-sm text-gray-400">Настроить каналы</div>
            </div>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/settings?tab=youtube')}
            className="flex items-center gap-4 p-4 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-xl transition-all text-left"
          >
            <div className="w-12 h-12 bg-red-500/30 rounded-xl flex items-center justify-center text-2xl">
              ⚙️
            </div>
            <div>
              <div className="font-semibold text-white">YouTube API</div>
              <div className="text-sm text-gray-400">Настроить интеграцию</div>
            </div>
          </motion.button>
        </div>
      </AnimatedCard>

      {/* Видео, требующие внимания */}
      {attentionVideos.length > 0 && (
        <AnimatedCard className="p-6 border-yellow-500/30 border-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">⚠️ Требуют внимания</h2>
            <button
              onClick={() => navigate('/youtube')}
              className="text-yellow-400 hover:text-yellow-300 text-sm font-medium transition-colors"
            >
              Смотреть все →
            </button>
          </div>
          <div className="space-y-3">
            {attentionVideos.map((video) => {
              let statusText = '';
              let statusColor = 'text-gray-400';
              let actionText = '';
              
              if (video.status === 'error' || video.status === 'publish_error') {
                statusText = '❌ Ошибка';
                statusColor = 'text-red-400';
                actionText = 'Исправить';
              } else if (video.status === 'transcribing' || video.status === 'generating') {
                statusText = '⏳ Обработка...';
                statusColor = 'text-yellow-400';
                actionText = 'Отслеживать';
              } else if (video.status === 'transcribed' && !video.youtube_url) {
                statusText = '✅ Готово к публикации';
                statusColor = 'text-green-400';
                actionText = 'Опубликовать';
              }
              
              return (
                <AnimatedCard
                  key={video.id}
                  onClick={() => navigate('/youtube')}
                  hover={true}
                  className="p-3 cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-500/30 to-orange-500/30 rounded-lg flex items-center justify-center text-xl">
                      {video.status === 'error' ? '❌' : video.status === 'transcribing' ? '⏳' : '✅'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white truncate">
                        {video.generated_title || video.file_name || 'Без названия'}
                      </div>
                      <div className={`text-sm ${statusColor}`}>
                        {statusText}
                      </div>
                    </div>
                    <div className="text-gray-500 text-sm">
                      {actionText}
                    </div>
                  </div>
                </AnimatedCard>
              );
            })}
          </div>
        </AnimatedCard>
      )}

      {/* Последние видео */}
      <AnimatedCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">🎬 Последние видео</h2>
          <button
            onClick={() => navigate('/youtube')}
            className="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
          >
            Смотреть все →
          </button>
        </div>

        {loading ? (
          <SkeletonLoader type="video-card" count={3} />
        ) : recentVideos.length > 0 ? (
          <div className="space-y-3">
            {recentVideos.map((video, i) => (
              <AnimatedCard
                key={video.id}
                onClick={() => navigate('/youtube')}
                hover={true}
                className="p-3 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-lg flex items-center justify-center text-xl">
                    🎥
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">
                      {video.generated_title || video.original_filename || 'Без названия'}
                    </div>
                    <div className="text-sm text-gray-400">
                      {video.transcription_status === 'completed' ? '✅ Транскрибировано' : 
                       video.transcription_status === 'processing' ? '⏳ Обработка...' : 
                       '📤 Загружено'}
                    </div>
                  </div>
                  <div className="text-gray-500 text-sm">
                    {new Date(video.created_at).toLocaleDateString('ru-RU')}
                  </div>
                </div>
              </AnimatedCard>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">📹</div>
            <p className="text-gray-400 mb-4">Пока нет загруженных видео</p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/youtube')}
              className="px-6 py-2 bg-purple-500/20 text-purple-400 rounded-xl border border-purple-500/30 hover:bg-purple-500/30 transition-all"
            >
              Загрузить первое видео
            </motion.button>
          </div>
        )}
      </AnimatedCard>

      {/* Полезные ссылки */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <h2 className="text-xl font-bold text-white mb-4">🔗 Полезные ссылки</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <a
            href="https://www.youtube.com/@AiMarketHub"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-xl transition-all"
          >
            <span className="text-2xl">🎬</span>
            <div>
              <div className="font-medium text-white">YouTube канал</div>
              <div className="text-sm text-gray-400">@AiMarketHub</div>
            </div>
          </a>
          <a
            href="https://t.me/ai_n8n_hub"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 rounded-xl transition-all"
          >
            <span className="text-2xl">💬</span>
            <div>
              <div className="font-medium text-white">Telegram группа</div>
              <div className="text-sm text-gray-400">Сообщество</div>
            </div>
          </a>
          <a
            href="https://t.me/solvillage"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 rounded-xl transition-all"
          >
            <span className="text-2xl">👨‍💻</span>
            <div>
              <div className="font-medium text-white">Разработчик</div>
              <div className="text-sm text-gray-400">Связаться</div>
            </div>
          </a>
        </div>
      </motion.div>
    </div>
  );
}
